import { WFComponent, navigate } from "@xatom/core";

declare global {
  interface Window {
    fsAttributes: any[];
  }
}
import { AuthAPI } from "../../services/api/auth";
import supabase from "../supbase";
import { websiteRoutes } from "../../config";

export class ComponentHandler {
  private components: WFComponent[];
  private componentElesList: HTMLElement[] = [];

  constructor() {
    if (this.hasFinsweetLoadMoreAdded()) {
      this.loadAndUpdateComponent();
    } else {
      this.init();
    }
  }

  private loadAndUpdateComponent(): void {
    window.fsAttributes = window.fsAttributes || [];

    window.fsAttributes.push([
      "cmsload",
      (listInstances) => {
        // The callback passes a `listInstances` array with all the `CMSList` instances on the page.
        const [listInstance] = listInstances;

        this.componentElesList = listInstance.items.map((item) => {
          return item.element as HTMLElement;
        });

        this.init();
      },
    ]);
  }

  hasFinsweetLoadMoreAdded() {
    const finsweetLoadMore = document.querySelector(
      `[fs-cmsfilter-element="list"] [ss-component-ele="card"]`
    );

    if (finsweetLoadMore) {
      return true;
    }

    return false;
  }

  public init() {
    if (!this.componentElesList || this.componentElesList.length === 0) {
      this.components = Array.from(
        document.querySelectorAll("[ss-webflow-component]")
      ).map((el) => new WFComponent(el));
    } else {
      this.components = Array.from(this.componentElesList).map((el) => {
        const $comp = el.querySelector("[ss-webflow-component]");
        return new WFComponent($comp);
      });
    }

    this.initButtons();
  }

  private async checkAuth(): Promise<boolean> {
    const { data } = await supabase.auth.getSession();
    return !!data.session;
  }

  private async getComponentCode(slug: string): Promise<string> {
    try {
      const response = await AuthAPI.getComponent(slug);

      if (response.status === 200) {
        const componentCode = response.data.componentJson;
        return componentCode;
      }
      throw new Error("Failed to fetch component");
    } catch (error) {
      console.error("Error fetching component:", error);
      throw error;
    }
  }

  private async copyToClipboard(
    text: string,
    type: string = "application/json"
  ): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);

      const copyJson = (e: ClipboardEvent) => {
        e.preventDefault();
        if (e.clipboardData) {
          e.clipboardData.setData(type, text);
        } else {
          console.error("ClipboardData is not available");
        }
      };
      document.addEventListener("copy", copyJson);
      document.execCommand("Copy");
      document.removeEventListener("copy", copyJson);
    } catch (err) {
      console.error("Failed to copy using clipboard API:", err);
      // Fallback for older browsers
      const copyJson = (e: ClipboardEvent) => {
        e.preventDefault();
        if (e.clipboardData) {
          e.clipboardData.setData(type, text);
        } else {
          console.error("ClipboardData is not available");
        }
      };
      document.addEventListener("copy", copyJson);
      document.execCommand("Copy");
      document.removeEventListener("copy", copyJson);
    }
  }

  private initButtons(): void {
    this.components.forEach((component) => {
      const copyBtn = component.getChildAsComponent(
        '[ss-webflow-component-ele="copy-btn"]'
      );
      const previewBtn = component.getChildAsComponent(
        '[ss-webflow-component-ele="preview-comp-btn"]'
      );
      const favBtn = component.getChildAsComponent(
        '[ss-webflow-component-ele="fav-comp-btn"]'
      );

      if (copyBtn) {
        this.initCopyButton(copyBtn, component);
      }

      if (previewBtn) {
        this.initPreviewButton(previewBtn, component);
      }

      if (favBtn) {
        this.initFavButton(favBtn);
      }
    });
  }

  public initCopyButton(copyBtn: WFComponent, component: WFComponent): void {
    const copyBtnPara = copyBtn.getChildAsComponent("p");
    const originalText = copyBtnPara.getText();

    copyBtn.on("click", async () => {
      // Check if user is logged in
      const isLoggedIn = await this.checkAuth();
      if (!isLoggedIn) {
        navigate(websiteRoutes.signup);
        return;
      }

      const slug = component.getAttribute("ss-webflow-component");
      const type = "application/json";

      if (!slug) {
        console.error("No component slug provided");
        return;
      }

      try {
        // Show loading state
        copyBtnPara.setText("Copying...");
        copyBtn.setAttribute("disabled", "true");

        // Get and copy component code
        const code = await this.getComponentCode(slug);
        await this.copyToClipboard(code, type);

        // Show success state
        copyBtnPara.setText("Copied!");

        // Reset button after 2 seconds
        setTimeout(() => {
          copyBtnPara.setText(originalText);
          copyBtn.removeAttribute("disabled");
        }, 2000);
      } catch (error) {
        console.error("Failed to copy:", error);
        copyBtnPara.setText("Failed to copy");
        setTimeout(() => {
          copyBtnPara.setText(originalText);
          copyBtn.removeAttribute("disabled");
        }, 2000);
      }
    });
  }

  private initPreviewButton(
    previewBtn: WFComponent,
    component: WFComponent
  ): void {
    previewBtn.on("click", () => {
      const componentId = component.getAttribute("preview-comp-link");

      if (componentId) {
        window.open(
          `${websiteRoutes.preview}?comp_id=${componentId}`,
          "_blank"
        );
      } else {
        console.error("No component ID provided");
      }
    });
  }

  private initFavButton(favBtn: WFComponent): void {
    favBtn.on("click", () => {
      console.log("Favorite button clicked");
    });
  }
}